import React from 'react';
import { Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import type { BatchJob } from '../types/batch';

interface BatchJobsListProps {
  jobs: BatchJob[];
  isLoading: boolean;
  error?: Error | null;
  onDownload: (jobId: string, outputFileId: string, apiPath?: string, downloadKind?: 'result' | 'error') => Promise<void>;
  defaultApiPath?: string;
}

const PROCESSING_STATUSES = ['queued', 'validating', 'in_progress', 'running', 'finalizing'];

// Utility functions
const formatDate = (timestamp: number): string => new Date(timestamp * 1000).toLocaleString();

const isJobProcessing = (job: BatchJob): boolean => 
  PROCESSING_STATUSES.includes(job.status.toLowerCase());

const getResultFileId = (job: BatchJob): string | undefined => {
  if (job.output_file_id) return job.output_file_id;
  // Partial jobs can have successful output rows even when the list API omits
  // output_file_id; the Batch API streams result content by canonical job id.
  return (job.request_counts?.completed ?? 0) > 0 ? job.id : undefined;
};

const getErrorFileId = (job: BatchJob): string | undefined => {
  if (job.error_file_id) return job.error_file_id;
  // Partial jobs can have error rows even when the list API omits error_file_id;
  // the Batch API serves the per-job error log as "<jobId>_errors".
  return (job.request_counts?.failed ?? 0) > 0 ? `${job.id}_errors` : undefined;
};

const getDownloadButtonLabel = (isDownloading: boolean, isProcessing: boolean, downloadKind: 'result' | 'error'): string => {
  if (isDownloading) return 'Downloading...';
  if (downloadKind === 'error') return 'Download Errors';
  return isProcessing ? 'Download Partial' : 'Download';
};

const getDownloadButtonTitle = (hasOutputFile: boolean, isProcessing: boolean, downloadKind: 'result' | 'error'): string => {
  if (downloadKind === 'error') return "Download error JSONL file";
  if (isProcessing) return "Download partial results (job still processing)";
  return hasOutputFile ? "Download output file" : "Try downloading results (output file may not be ready yet)";
};

const formatErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
  return String(error);
};

const useStyles = makeStyles({
  errorContainer: {
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedForeground1}`,
    borderRadius: tokens.borderRadiusSmall,
  },
  errorTitle: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: 'bold',
    marginBottom: tokens.spacingVerticalXS,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  placeholderText: {
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  jobList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  jobCard: {
    padding: tokens.spacingVerticalL,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  jobHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'start',
    gap: tokens.spacingHorizontalL,
  },
  jobContent: {
    flex: '1',
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalXS,
  },
  jobId: {
    fontSize: tokens.fontSizeBase300,
    fontFamily: 'monospace',
    marginBottom: tokens.spacingVerticalM,
    wordBreak: 'break-all',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: tokens.spacingVerticalM,
  },
  detailValue: {
    fontSize: tokens.fontSizeBase300,
  },
  monoValue: {
    fontSize: tokens.fontSizeBase200,
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  statusColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    alignItems: 'flex-end',
  },
  statusBadgeBase: {
    paddingTop: tokens.spacingVerticalSNudge,
    paddingBottom: tokens.spacingVerticalSNudge,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusSmall,
    fontWeight: 'bold',
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
  },
  statusCompleted: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
  },
  statusFailed: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
  },
  statusCancelled: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
  },
  statusDefault: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },

  warningBanner: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${tokens.colorPaletteYellowForeground1}`,
  },
  warningText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteYellowForeground2,
  },
  infoBanner: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${tokens.colorBrandForeground1}`,
  },
  infoText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorBrandForeground1,
  },
  errorBanner: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusSmall,
  },
  errorBannerText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    wordBreak: 'break-word',
  },
});

export default function BatchJobsList({ jobs, isLoading, error, onDownload, defaultApiPath }: BatchJobsListProps) {
  const styles = useStyles();
  const [downloadingKeys, setDownloadingKeys] = React.useState<Set<string>>(() => new Set());
  
  const getStatusBadgeClass = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'completed' || statusLower === 'succeeded') return styles.statusCompleted;
    if (statusLower === 'failed') return styles.statusFailed;
    if (statusLower === 'cancelled') return styles.statusCancelled;
    return styles.statusDefault;
  };

  const handleDownload = async (jobId: string, outputFileId: string | undefined, downloadKind: 'result' | 'error') => {
    if (!outputFileId) {
      alert(downloadKind === 'result' ? 'Output file is not available yet.' : 'Error file is not available yet.');
      return;
    }

    const key = `${jobId}:${downloadKind}`;
    setDownloadingKeys(prev => new Set([...prev, key]));
    try {
      const job = jobs.find(j => j.id === jobId);
      const apiPath = job?.apiPath || job?.metadata?.api_path || defaultApiPath;
      await onDownload(jobId, outputFileId, apiPath, downloadKind);
    } catch (error) {
      console.error('Download failed:', error);
      alert(`Download failed: ${error instanceof Error ? error.message : 'Unknown error'}. The file may not be ready yet.`);
    } finally {
      setDownloadingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorTitle}>Error loading jobs</div>
        <div className={styles.errorText}>{error.message}</div>
      </div>
    );
  }

  return (
    <div>
      {isLoading && jobs.length === 0 ? (
        <div className={styles.placeholderText}>Loading jobs...</div>
      ) : jobs.length === 0 ? (
        <div className={styles.placeholderText}>
          No batch jobs found for the current user.
        </div>
      ) : (
        <div className={styles.jobList}>
          {jobs.map((job) => {
            const resultFileId = getResultFileId(job);
            const errorFileId = getErrorFileId(job);

            return (
              <div key={job.id} className={styles.jobCard}>
              <div className={styles.jobHeader}>
                <div className={styles.jobContent}>
                  <div className={styles.fieldLabel}>Job ID</div>
                  <div className={styles.jobId}>
                    {job.id}
                  </div>
                  
                  <div className={styles.detailsGrid}>
                    <div>
                      <div className={styles.fieldLabel}>Created</div>
                      <div className={styles.detailValue}>{formatDate(job.created_at)}</div>
                    </div>
                    
                    {job.completed_at && (
                      <div>
                        <div className={styles.fieldLabel}>Completed</div>
                        <div className={styles.detailValue}>{formatDate(job.completed_at)}</div>
                      </div>
                    )}
                    
                    {job.failed_at && (
                      <div>
                        <div className={styles.fieldLabel}>Failed</div>
                        <div className={styles.detailValue}>{formatDate(job.failed_at)}</div>
                      </div>
                    )}
                    
                    {job.input_file_id && (
                      <div>
                        <div className={styles.fieldLabel}>Input File ID</div>
                        <div className={styles.monoValue}>
                          {job.input_file_id}
                        </div>
                      </div>
                    )}
                    
                    {job.output_file_id && (
                      <div>
                        <div className={styles.fieldLabel}>Output File ID</div>
                        <div className={styles.monoValue}>
                          {job.output_file_id}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className={styles.statusColumn}>
                  <div className={mergeClasses(styles.statusBadgeBase, getStatusBadgeClass(job.status))}>
                    {job.status.toUpperCase()}
                  </div>
                  
                  {resultFileId && (
                    <Button
                      appearance="primary"
                      size="small"
                      onClick={() => handleDownload(job.id, resultFileId, 'result')}
                      disabled={downloadingKeys.has(`${job.id}:result`)}
                      title={getDownloadButtonTitle(true, isJobProcessing(job), 'result')}
                    >
                      {getDownloadButtonLabel(downloadingKeys.has(`${job.id}:result`), isJobProcessing(job), 'result')}
                    </Button>
                  )}

                  {errorFileId && (
                    <Button
                      appearance="secondary"
                      size="small"
                      onClick={() => handleDownload(job.id, errorFileId, 'error')}
                      disabled={downloadingKeys.has(`${job.id}:error`)}
                      title={getDownloadButtonTitle(true, false, 'error')}
                    >
                      {getDownloadButtonLabel(downloadingKeys.has(`${job.id}:error`), false, 'error')}
                    </Button>
                  )}
                </div>
              </div>
              
              {isJobProcessing(job) && !job.output_file_id && (
                <div className={styles.warningBanner}>
                  <div className={styles.warningText}>
                    <strong>Output not ready yet:</strong> This job is processing. You can try downloading, but results may not be available yet.
                  </div>
                </div>
              )}
              
              {isJobProcessing(job) && job.output_file_id && (
                <div className={styles.infoBanner}>
                  <div className={styles.infoText}>
                    <strong>Partial results available:</strong> This job is still processing, but you can download the results generated so far.
                  </div>
                </div>
              )}
              
              {job.error !== undefined && job.error !== null && (
                <div className={styles.errorBanner}>
                  <div className={styles.errorBannerText}>
                    <strong>Error:</strong> {formatErrorMessage(job.error)}
                  </div>
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
